import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import LessonDetail from './pages/LessonDetail';
import Chat from './pages/Chat';
import Messages from './pages/Messages';
import Connect from './pages/Connect';
import Progress from './pages/Progress';
import ForgotPassword from './pages/ForgotPassword';
import LandingPage from './pages/LandingPage';
import { PrivacyPage, TermsPage, ContactPage } from './pages/LegalPages';
import { ScrollToTop } from './components/ScrollToTop';

// Components
import { Onboarding } from './components/Onboarding';
import { OfflineBanner } from './components/OfflineBanner';

const LessonEditor = lazy(() => import('./pages/LessonEditor'));
const TeacherResponses = lazy(() => import('./pages/TeacherResponses'));
const ManagePage = lazy(() => import('./pages/ManagePage'));
const Admin = lazy(() => import('./pages/Admin'));
const Settings = lazy(() => import('./pages/Settings'));
const AttendanceReport = lazy(() => import('./pages/AttendanceReport'));
const Search = lazy(() => import('./pages/Search'));
const TeacherSetup = lazy(() => import('./pages/TeacherSetup'));
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard'));
const SecurityLog = lazy(() => import('./pages/SecurityLog'));

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireTeacher = false }) => {
    const { isAuthenticated, loading, isAdmin, isTeacher, needsGroupSetup } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    if (needsGroupSetup) {
        return <Navigate to="/teacher-setup" replace />;
    }

    if (requireAdmin && !isAdmin) {
        return <Navigate to="/" replace />;
    }

    if (requireTeacher && !isTeacher && !isAdmin) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Route specifically for teacher setup — only accessible when needs_group_setup is true
const TeacherSetupRoute = ({ children }) => {
    const { isAuthenticated, loading, needsGroupSetup } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    if (!needsGroupSetup) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, needsGroupSetup } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isAuthenticated) {
        if (needsGroupSetup) {
            return <Navigate to="/teacher-setup" replace />;
        }
        return <Navigate to="/" replace />;
    }

    return children;
};

// Onboarding Overlay - Shows tutorial for new users
const OnboardingOverlay = () => {
    const { needsOnboarding, needsGroupSetup, completeOnboarding } = useAuth();
    
    if (!needsOnboarding || needsGroupSetup) return null;
    
    return <Onboarding onComplete={completeOnboarding} />;
};

// Home Route — Landing page when not authenticated, Dashboard when authenticated
const HomeRoute = () => {
    const { isAuthenticated, loading, needsGroupSetup } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }
    if (isAuthenticated) {
        if (needsGroupSetup) return <Navigate to="/teacher-setup" replace />;
        return <Dashboard />;
    }
    return <LandingPage />;
};

function AppRoutes() {
    return (
        <>
            <OnboardingOverlay />
            <ScrollToTop />
            <Suspense fallback={<div className="min-h-screen" data-testid="route-loading" />}>
            <Routes>
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />
            <Route
                path="/register"
                element={
                    <PublicRoute>
                        <Register />
                    </PublicRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <PublicRoute>
                        <ForgotPassword />
                    </PublicRoute>
                }
            />

            {/* Teacher Setup Route — only for teachers needing group creation */}
            <Route
                path="/teacher-setup"
                element={
                    <TeacherSetupRoute>
                        <TeacherSetup />
                    </TeacherSetupRoute>
                }
            />

            {/* Protected Routes */}
            <Route path="/" element={<HomeRoute />} />
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/courses"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/progress"
                element={
                    <ProtectedRoute>
                        <Progress />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/courses/:courseId"
                element={
                    <ProtectedRoute>
                        <CourseDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/lessons/:lessonId"
                element={
                    <ProtectedRoute>
                        <LessonDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/lessons/:lessonId/edit"
                element={
                    <ProtectedRoute requireTeacher>
                        <LessonEditor />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/lessons/:lessonId/responses"
                element={
                    <ProtectedRoute requireTeacher>
                        <TeacherResponses />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/chat"
                element={<Navigate to="/connect" replace />}
            />
            <Route
                path="/messages"
                element={<Navigate to="/connect?tab=direct" replace />}
            />
            <Route
                path="/connect"
                element={
                    <ProtectedRoute>
                        <Connect />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute requireAdmin>
                        <Navigate to="/manage" replace />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/manage"
                element={
                    <ProtectedRoute requireTeacher>
                        <ManagePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/teacher-dashboard"
                element={<Navigate to="/manage" replace />}
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <Settings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/attendance"
                element={<Navigate to="/manage?tab=attendance" replace />}
            />
            <Route
                path="/search"
                element={
                    <ProtectedRoute>
                        <Search />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/security-log"
                element={<Navigate to="/manage?tab=security" replace />}
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
            </Suspense>
        </>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <OfflineBanner />
                <AppRoutes />
                <Toaster position="top-center" richColors duration={3000} />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
